const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  companyName?: string;
  sortBy?: 'uploadedAt' | 'fileName' | 'calculatedAt' | 'processedAt';
  sortDirection?: 'asc' | 'desc';
  status?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FileInfo {
  id: string;
  fileName: string;
  originalName: string;
  fileSize: number;
  uploadedAt: Date;
  uploadedBy: {
    id: number;
    name: string;
    email: string;
  };
  companyId: number;
  companyName: string;
  status: string;
  userType: string;
  mappings?: Record<string, string>;
  settings?: Record<string, any>;
}

export interface ProcessedFile extends FileInfo {
  processedAt: Date;
  processedBy: {
    id: number;
    name: string;
    email: string;
  };
  schema: string;
  settings: {
    hasHeader: boolean;
    useStreetDistances: boolean;
    standard: string;
    schemaName?: string;
  };
  progress: number;
  rowsProcessed?: number;
  totalRows?: number;
  estimatedTime?: string;
  calculationId?: string;
}

export interface CalculatedFile extends ProcessedFile {
  calculatedAt: Date;
  calculatedBy: {
    id: number;
    name: string;
    email: string;
  };
  results?: {
    totalTOT: number;
    totalOPS: number;
    totalENE: number;
    totalDistance: number;
    totalWeight: number;
    totalTKM: number;
    totalShipments: number;
    averageCO2: number;
    averageEI: number;
    errorCount?: number;
    shipments?: any[];
  };
}

export interface LegacyFile {
  id: number;
  displayName: string;
  companyId: number | null;
  companyName: string;
  uploadedAt: Date | null;
  scheme: string | null;
  fileSize: number;
}

export interface LegacyFileParams {
  page?: number;
  limit?: number;
  search?: string;
  companyId?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

class FileService {
  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const token = localStorage.getItem('authToken');
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return response.json();
  }

  async getUploadedFiles(params?: PaginationParams): Promise<PaginatedResponse<FileInfo>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.search) query.set('search', params.search);
    if (params?.companyName) query.set('companyName', params.companyName);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortDirection) query.set('sortDirection', params.sortDirection);
    const qs = query.toString();

    const result = await this.makeRequest(`/excel/files/uploaded${qs ? '?' + qs : ''}`);

    return {
      data: result.data.map((file: any) => ({
        ...file,
        uploadedAt: new Date(file.uploadedAt),
      })),
      pagination: result.pagination,
    };
  }

  async getFinalFiles(params?: PaginationParams): Promise<PaginatedResponse<FileInfo>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.search) query.set('search', params.search);
    if (params?.companyName) query.set('companyName', params.companyName);
    const qs = query.toString();

    const result = await this.makeRequest(`/excel/files/final${qs ? '?' + qs : ''}`);

    return {
      data: result.data.map((file: any) => ({
        ...file,
        uploadedAt: new Date(file.uploadedAt),
      })),
      pagination: result.pagination,
    };
  }

  async getProcessedFiles(params?: PaginationParams): Promise<PaginatedResponse<ProcessedFile>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.search) query.set('search', params.search);
    if (params?.companyName) query.set('companyName', params.companyName);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortDirection) query.set('sortDirection', params.sortDirection);
    if (params?.status) query.set('status', params.status);
    const qs = query.toString();

    const result = await this.makeRequest(`/excel/files/processed${qs ? '?' + qs : ''}`);

    return {
      data: result.data.map((file: any) => ({
        ...file,
        processedAt: new Date(file.processedAt),
      })),
      pagination: result.pagination,
    };
  }

  async getCalculatedFiles(params?: PaginationParams): Promise<PaginatedResponse<CalculatedFile>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.search) query.set('search', params.search);
    if (params?.companyName) query.set('companyName', params.companyName);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortDirection) query.set('sortDirection', params.sortDirection);
    const qs = query.toString();

    const result = await this.makeRequest(`/excel/files/calculated${qs ? '?' + qs : ''}`);

    return {
      data: result.data.map((file: any) => ({
        ...file,
        calculatedAt: new Date(file.calculatedAt),
      })),
      pagination: result.pagination,
    };
  }

  async getLegacyFiles(params?: LegacyFileParams): Promise<PaginatedResponse<LegacyFile>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.search) query.set('search', params.search);
    if (params?.companyId) query.set('companyId', params.companyId.toString());
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortDirection) query.set('sortDirection', params.sortDirection);
    const qs = query.toString();

    const result = await this.makeRequest(`/excel/files/legacy${qs ? '?' + qs : ''}`);

    return {
      data: result.data.map((file: any) => ({
        ...file,
        uploadedAt: file.uploadedAt ? new Date(file.uploadedAt) : null,
      })),
      pagination: result.pagination,
    };
  }

  async downloadLegacyFile(id: number): Promise<void> {
    const token = localStorage.getItem('authToken');
    await this.fetchAndSaveFile(`${API_BASE_URL}/excel/files/legacy/${id}/download`, token || '');
  }

  async getFileBlob(fileId: string): Promise<Blob> {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/excel/download/${fileId}`, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download file');
    }
    return await response.blob();
  }

  private resolveDownloadFilename(headers: Headers): string {
    const contentDisposition = headers.get('content-disposition');
    if (contentDisposition) {
      // RFC 5987 form first — it is the only one that survives non-ASCII names.
      const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
      if (encodedMatch) {
        try {
          return decodeURIComponent(encodedMatch[1]);
        } catch {
          // Malformed encoding — fall through to the ASCII form.
        }
      }
      const filenameMatch = /filename="(.+?)"/.exec(contentDisposition);
      if (filenameMatch) {
        return filenameMatch[1];
      }
    }
    // Fallback: derive extension from content type
    const contentType = headers.get('content-type') || '';
    const ext = contentType.includes('spreadsheetml') ? '.xlsx' : '.csv';
    return `download_${Date.now()}${ext}`;
  }

  private triggerBlobDownload(blob: Blob, filename: string): void {
    const url = globalThis.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = filename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    globalThis.URL.revokeObjectURL(url);
  }

  private async fetchAndSaveFile(href: string, token: string): Promise<void> {
    try {
      const response = await fetch(href, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const filename = this.resolveDownloadFilename(response.headers);
      this.triggerBlobDownload(blob, filename);
    } catch (error) {
      console.error('Download error:', error);
      throw new Error('Failed to download file');
    }
  }

  async downloadFile(fileId: string): Promise<void> {
    const token = localStorage.getItem('authToken');

    // Create a direct link to download the file
    const link = document.createElement('a');
    link.href = `${API_BASE_URL}/excel/download/${fileId}`;
    link.setAttribute('download', '');

    // Add authorization header by opening in new window if needed
    // For simplicity, we'll use the direct link approach
    if (token) {
      // For file downloads, we need to handle auth differently
      await this.fetchAndSaveFile(link.href, token);
    } else {
      // Fallback for no auth
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }

  async deleteFile(fileId: string): Promise<void> {
    await this.makeRequest(`/excel/files/${fileId}`, {
      method: 'DELETE',
    });
  }
}

export const fileService = new FileService();